import React from "react";
import { Box, Typography, Button, Avatar } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ProtectedImage from "../../components/ProtectedImage";
import FormDrawer from "../../components/layout/FormDrawer";
import { BRAND, primaryButtonSx } from "../../components/auth/authTheme";

const DetailItem = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BRAND.border}`,
      bgcolor: "#fff",
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: BRAND.orangeLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: BRAND.orange,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: BRAND.muted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: value ? BRAND.text : BRAND.muted,
          fontWeight: value ? 500 : 400,
          mt: 0.25,
          wordBreak: "break-word",
        }}
      >
        {value || "Not provided"}
      </Typography>
    </Box>
  </Box>
);

const CompanyViewModal = ({ show, handleClose, company }) => {
  if (!company) return null;

  const hasLogo = Boolean(company.company_logo);

  return (
    <FormDrawer
      open={show}
      onClose={handleClose}
      title={company.company_name}
      subtitle="Organization details"
      footer={
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          sx={{ ...primaryButtonSx, px: 4 }}
        >
          Close
        </Button>
      }
    >
      <Box
        sx={{
          textAlign: "center",
          py: 3,
          px: 2,
          mb: 3,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, #243b5e 60%, rgba(255,106,0,0.25) 100%)`,
        }}
      >
        {hasLogo ? (
          <Box
            sx={{
              width: 96,
              height: 96,
              mx: "auto",
              mb: 2,
              borderRadius: "16px",
              overflow: "hidden",
              border: "3px solid rgba(255,255,255,0.3)",
              bgcolor: "#fff",
            }}
          >
            <ProtectedImage
              path={company.company_logo}
              alt={company.company_name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        ) : (
          <Avatar
            sx={{
              width: 96,
              height: 96,
              mx: "auto",
              mb: 2,
              bgcolor: BRAND.orange,
              fontSize: "2rem",
              fontWeight: 700,
            }}
          >
            <BusinessOutlinedIcon sx={{ fontSize: 48 }} />
          </Avatar>
        )}

        <Typography
          variant="h6"
          sx={{ color: "#fff", fontWeight: 700, mb: 0.5 }}
        >
          {company.company_name}
        </Typography>
        {company.email && (
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            {company.email}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <DetailItem
          icon={<EmailOutlinedIcon fontSize="small" />}
          label="Email"
          value={company.email}
        />
        <DetailItem
          icon={<LocationOnOutlinedIcon fontSize="small" />}
          label="Location"
          value={company.location}
        />
        <DetailItem
          icon={<GroupsOutlinedIcon fontSize="small" />}
          label="Employee Strength"
          value={
            company.employee_strength != null && company.employee_strength !== ""
              ? String(company.employee_strength)
              : null
          }
        />
      </Box>

      <DetailItem
        icon={<DescriptionOutlinedIcon fontSize="small" />}
        label="Description"
        value={company.description}
      />
    </FormDrawer>
  );
};

export default CompanyViewModal;
